import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toggleSidebar, toggleDarkMode } from '../../store/slices/uiSlice';
import { setCurrentChannel } from '../../store/slices/chatSlice';
import Sidebar from '../chat/Sidebar';
import { CloseIcon, MenuIcon, PluginsIcon } from '../chat/SidebarIcons';
import CuminIntegrationPanel from '../cumin/CuminIntegrationPanel';
import JiraIntegrationPanel from '../jira/JiraIntegrationPanel';
import GoogleCalendarPanel from '../google/GoogleCalendarPanel';
import GoogleDrivePanel from '../google/GoogleDrivePanel';
import { getJiraCredentials } from '../../services/jiraService';
import { getGooglePluginStatus } from '../../services/googlePluginService';
import { API_URL } from '../../config/api';

const CuminMark = ({ className = 'w-10 h-10' }) => (
  <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <rect width="32" height="32" rx="8" fill="#0f766e" />
    <path
      d="M9 16.5c0-4 3.1-7.5 7-7.5 2.6 0 4.9 1.4 6.2 3.5"
      stroke="white"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
    <path
      d="M23 15.5c0 4-3.1 7.5-7 7.5-2.6 0-4.9-1.4-6.2-3.5"
      stroke="white"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
    <circle cx="16" cy="16" r="2.4" fill="white" />
  </svg>
);

const JiraMark = ({ className = 'w-10 h-10' }) => (
  <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <rect width="32" height="32" rx="8" fill="#1868DB" />
    <path
      d="M16.2 7.2l8.6 8.6c.9.9.9 2.4 0 3.3L16.2 28c-.1-.9-.1-7.4 0-10.4.1-3.1.1-9.5 0-10.4z"
      fill="#2684FF"
    />
    <path
      d="M16.2 7.2L7.6 15.8c-.9.9-.9 2.4 0 3.3l8.6 8.6c.1-.9.1-7.4 0-10.4-.1-3.1-.1-9.5 0-10.4z"
      fill="white"
    />
  </svg>
);

const GoogleCalendarMark = ({ className = 'w-10 h-10' }) => (
  <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <rect width="32" height="32" rx="8" fill="#fff" />
    <rect x="4" y="6" width="24" height="20" rx="3" fill="#1A73E8" />
    <rect x="4" y="6" width="24" height="6" fill="#188038" />
    <text x="16" y="22" textAnchor="middle" fill="white" fontSize="10" fontWeight="700" fontFamily="sans-serif">
      31
    </text>
  </svg>
);

const GoogleDriveMark = ({ className = 'w-10 h-10' }) => (
  <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <rect width="32" height="32" rx="8" fill="#F1F3F4" />
    <path d="M10.2 22.5L16 12.2l5.8 10.3H10.2z" fill="#FFBA00" />
    <path d="M7 22.5l4.2-7.4L16 22.5H7z" fill="#188038" />
    <path d="M16 22.5l4.8-7.4L25 22.5H16z" fill="#1A73E8" />
    <path d="M11.2 15.1L16 7.5l4.8 7.6H11.2z" fill="#EA4335" />
  </svg>
);

const plugins = [
  {
    id: 'cumin',
    name: 'CUMIN',
    description: 'Create and track CUMIN issues from WorkLoop.',
    Icon: CuminMark,
  },
  {
    id: 'jira',
    name: 'Jira',
    description: 'Sign in with Atlassian (SSO supported) and see tickets assigned to you.',
    Icon: JiraMark,
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    description: 'Two-way sync between WorkLoop meetings and Google Calendar.',
    Icon: GoogleCalendarMark,
  },
  {
    id: 'google-drive',
    name: 'Google Drive',
    description: 'Upload images and documents, then share a Drive link.',
    Icon: GoogleDriveMark,
  },
];

const IntegrationsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { sidebarOpen, darkMode } = useSelector((state) => state.ui);
  const { currentChannel } = useSelector((state) => state.chat);
  const [connected, setConnected] = useState({
    cumin: false,
    jira: false,
    'google-calendar': false,
    'google-drive': false,
  });
  const [loadingStatus, setLoadingStatus] = useState(true);
  const activePlugin = searchParams.get('plugin');

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    const token = localStorage.getItem('token');
    const next = { cumin: false, jira: false, 'google-calendar': false, 'google-drive': false };
    try {
      const response = await axios.get(`${API_URL}/cumin/credentials`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data || {};
      next.cumin = data.isConnected === true || data.connected === true;
    } catch {
      next.cumin = false;
    }
    try {
      const data = await getJiraCredentials();
      next.jira = Boolean(data?.isConnected);
    } catch {
      next.jira = false;
    }
    try {
      const google = await getGooglePluginStatus();
      next['google-calendar'] = Boolean(google?.calendarConnected);
      next['google-drive'] = Boolean(google?.driveConnected);
    } catch {
      next['google-calendar'] = false;
      next['google-drive'] = false;
    }
    setConnected(next);
    setLoadingStatus(false);
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const openPlugin = (id) => {
    setSearchParams({ plugin: id });
  };

  const closePlugin = () => {
    setSearchParams({});
    loadStatus();
  };

  const handleCuminStatus = useCallback((isConnected) => {
    setConnected((prev) => (
      prev.cumin === Boolean(isConnected) ? prev : { ...prev, cumin: Boolean(isConnected) }
    ));
  }, []);

  const handleJiraStatus = useCallback((isConnected) => {
    setConnected((prev) => (
      prev.jira === Boolean(isConnected) ? prev : { ...prev, jira: Boolean(isConnected) }
    ));
  }, []);

  const handleGoogleCalendarStatus = useCallback((isConnected) => {
    setConnected((prev) => (
      prev['google-calendar'] === Boolean(isConnected)
        ? prev
        : { ...prev, 'google-calendar': Boolean(isConnected) }
    ));
  }, []);

  const handleGoogleDriveStatus = useCallback((isConnected) => {
    setConnected((prev) => (
      prev['google-drive'] === Boolean(isConnected)
        ? prev
        : { ...prev, 'google-drive': Boolean(isConnected) }
    ));
  }, []);

  const active = plugins.find((plugin) => plugin.id === activePlugin);

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => dispatch(toggleSidebar())}
          onKeyDown={(e) => e.key === 'Escape' && dispatch(toggleSidebar())}
          role="button"
          tabIndex={0}
          aria-label="Close menu"
        />
      )}

      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          sidebarOpen
            ? 'w-80 fixed lg:relative left-0 top-0 h-full z-[60] lg:z-[60] shadow-xl lg:shadow-none'
            : 'w-0 lg:w-16 relative z-[60]'
        }`}
      >
        <Sidebar
          currentChannel={currentChannel}
          onChannelSelect={(channel) => {
            if (!channel) return;
            dispatch(setCurrentChannel(channel));
            setTimeout(() => navigate('/chat'), 50);
          }}
          onLogout={() => {
            window.location.href = '/login';
          }}
          onToggleSidebar={() => dispatch(toggleSidebar())}
          onToggleDarkMode={() => dispatch(toggleDarkMode())}
          sidebarOpen={sidebarOpen}
          darkMode={darkMode}
        />
      </div>

      <div className="flex-1 flex flex-col relative min-w-0 overflow-y-auto">
        {!sidebarOpen && (
          <button
            type="button"
            className="lg:hidden absolute left-2 top-2 p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
            onClick={() => dispatch(toggleSidebar())}
            aria-label="Open menu"
          >
            <MenuIcon className="w-5 h-5" />
          </button>
        )}

        <div className="max-w-4xl mx-auto w-full p-6 lg:p-10">
          <div className="flex items-center gap-3 mb-2">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200">
              <PluginsIcon className="w-6 h-6" />
            </span>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Plugins</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Connect tools to WorkLoop. Click a plugin to open its integration window.
              </p>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
            {plugins.map((plugin) => {
              const isIntegrated = Boolean(connected[plugin.id]);
              const Icon = plugin.Icon;
              return (
                <button
                  key={plugin.id}
                  type="button"
                  onClick={() => openPlugin(plugin.id)}
                  className="group relative flex flex-col items-center text-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-6 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition"
                >
                  <Icon className="w-12 h-12" />
                  <span className="mt-3 text-sm font-bold text-gray-900 dark:text-white">
                    {plugin.name}
                  </span>
                  <span className="mt-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                    {plugin.description}
                  </span>
                  <span
                    className={`mt-3 inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                      loadingStatus
                        ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
                        : isIntegrated
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                          : 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
                    }`}
                  >
                    {loadingStatus ? 'Checking…' : isIntegrated ? 'Integrated' : 'Not connected'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {active ? (
        <div className="fixed inset-0 lg:left-16 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closePlugin}>
          <div
            className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white dark:bg-gray-900 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 px-5 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <active.Icon className="w-8 h-8 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{active.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {connected[active.id] ? 'Integrated' : 'Connect this plugin'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closePlugin}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close integration window"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              {active.id === 'cumin' ? (
                <CuminIntegrationPanel embedded onStatusChange={handleCuminStatus} />
              ) : active.id === 'jira' ? (
                <JiraIntegrationPanel embedded onStatusChange={handleJiraStatus} />
              ) : active.id === 'google-calendar' ? (
                <GoogleCalendarPanel onStatusChange={handleGoogleCalendarStatus} />
              ) : (
                <GoogleDrivePanel onStatusChange={handleGoogleDriveStatus} />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default IntegrationsPage;
